import { useEffect, useRef } from 'react';
import type { JobTaskView } from '@codebox/shared';

/**
 * The build checklist as a carousel: the step being worked on sits in the middle, finished steps
 * drift up out of the way, and everything still to come waits below.
 *
 * A plain list made a twelve-step plan a wall of text where the one line that mattered — what is
 * happening RIGHT NOW — was wherever it happened to fall. Centring the active row means the eye
 * never has to look for it, and the rows either side give it context without competing.
 *
 * Everything moves: rows slide as the active index changes, the spinner becomes a tick, and the
 * whole strip is masked at both edges so items fade out instead of being chopped off mid-word.
 */

const ROW_HEIGHT = 56; // must match the row's min-height below — the transform is computed from it
const VISIBLE = 5; // rows shown at once; the rest are masked away

export interface TaskCarouselProps {
  tasks: JobTaskView[];
  /** Sparky's line for the active row; falls back to the task title. */
  narration?: string | null;
}

export function TaskCarousel({ tasks, narration }: TaskCarouselProps) {
  const liveRef = useRef<HTMLParagraphElement>(null);

  /*
   * Centre on the running step. Once everything has run, hold at the last row rather than snapping
   * back to the top — the final state should read as "finished", not "about to start".
   */
  const runningIndex = tasks.findIndex(t => t.status === 'running');
  const firstUnfinished = tasks.findIndex(t => t.status === 'planned');
  const activeIndex =
    runningIndex >= 0 ? runningIndex : firstUnfinished >= 0 ? firstUnfinished : Math.max(0, tasks.length - 1);

  const active = tasks[activeIndex];
  const label = narration ?? active?.title ?? '';

  /* Screen readers get the change announced; the visual centring is meaningless to them. */
  useEffect(() => {
    if (liveRef.current) liveRef.current.textContent = label;
  }, [label]);

  if (tasks.length === 0) return null;

  return (
    <div className="w-full">
      <div
        className="relative overflow-hidden"
        style={{
          height: ROW_HEIGHT * VISIBLE,
          /* the fade: fully transparent at both edges, solid through the middle band */
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
        }}
      >
        <ul
          className="absolute left-0 right-0 transition-transform duration-500 ease-out"
          style={{
            /* park the active row in the middle band */
            transform: `translateY(${ROW_HEIGHT * (Math.floor(VISIBLE / 2) - activeIndex)}px)`,
          }}
        >
          {tasks.map((task, index) => {
            const distance = Math.abs(index - activeIndex);
            const isActive = index === activeIndex;
            const isDone = task.status === 'done' || task.status === 'skipped';
            const failed = task.status === 'failed';
            return (
              <li
                key={task.id}
                className="flex items-center justify-center gap-3 px-1 transition-all duration-500 ease-out"
                style={{
                  minHeight: ROW_HEIGHT,
                  // near rows stay legible, far rows recede — depth without a scrollbar
                  opacity: isActive ? 1 : Math.max(0.25, 1 - distance * 0.3),
                  transform: `scale(${isActive ? 1.06 : Math.max(0.9, 1 - distance * 0.04)})`,
                  transformOrigin: 'center center',
                }}
              >
                <StepMark active={isActive} done={isDone} failed={failed} />
                <span
                  className={[
                    'min-w-0 max-w-[75%] truncate transition-all duration-500',
                    isActive ? 'text-[15px] font-bold text-ink' : 'text-sm font-semibold text-muted',
                  ].join(' ')}
                >
                  {task.title}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Announced to screen readers only — sighted users already see the active row centered and
          bold, so repeating it below just doubled the same words on screen. */}
      <p ref={liveRef} aria-live="polite" className="sr-only">
        {label}
      </p>
    </div>
  );
}

/** Spinner while running, tick when done — the same mark morphing, never a hard swap. */
function StepMark({ active, done, failed }: { active: boolean; done: boolean; failed: boolean }) {
  const base = 'grid h-6 w-6 shrink-0 place-items-center rounded-full transition-all duration-500';

  if (failed) {
    return (
      <span className={`${base} bg-danger/15 text-danger`} aria-label="did not finish">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </span>
    );
  }
  if (done) {
    return (
      <span className={`${base} anim-pop bg-accent text-accent-ink`} aria-label="done">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 12.5l5.5 5.5L20 7" />
        </svg>
      </span>
    );
  }
  if (active) {
    return (
      <span className={`${base} text-accent`} role="status" aria-label="in progress">
        <svg width="20" height="20" viewBox="0 0 20 20" className="animate-spin" aria-hidden="true">
          <circle cx="10" cy="10" r="7.5" fill="none" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2.5" />
          <path d="M10 2.5a7.5 7.5 0 0 1 7.5 7.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return <span className={`${base} border border-line`} aria-label="not started yet" />;
}
